import React from 'react'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { Checkbox } from '@mui/material'

import BaseModal from '../BaseModal2'
import ModalTextField from '../../../atoms/ModalTextField'
import EndControls from '../EndControls'

import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { createCategory } from '@SonarCX/slices/category'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'

import useStyles from './styles'

const CategoryModal = ({ open, onClose }) => {
  const dispatch = useAppDispatch()
  const hasPermission = useAccess()
  const { setAction, setReason, setV2 } = useAreYouSure()

  const [name, setName] = React.useState('')
  const [isPrivate, setIsPrivate] = React.useState(true)

  const canMakePrivate = React.useMemo(() => {
    return hasPermission(PERMS.TasksAdmin)
  }, [hasPermission])

  const changed = React.useMemo(() => {
    return name !== '' || !isPrivate
  }, [name, isPrivate])

  const classes = useStyles()

  React.useEffect(() => {
    if (!canMakePrivate) {
      setIsPrivate(true)
    }
  }, [canMakePrivate])

  const showError = React.useCallback(
    message => {
      dispatch(AddErrorAlert({ err: { message } }))
    },
    [dispatch]
  )

  const handleClose = React.useCallback(() => {
    setName('')
    setIsPrivate(true)
    onClose()
  }, [onClose])

  const handleCreate = React.useCallback(async () => {
    if (!name.length) {
      showError('Name cannot be empty')
      return
    }

    const category = {
      name,
      private: isPrivate,
      flow_order: null
    }

    try {
      await dispatch(createCategory(category))
      dispatch(AddSuccessAlert('Category created.'))
      handleClose()
    } catch (err) {
      console.error('Error creating category', err)
      dispatch(AddErrorAlert({ err }))
    }
  }, [showError, name, isPrivate, handleClose, dispatch])

  return (
    <>
      {/* @ts-ignore */}
      <BaseModal
        open={open}
        onClose={() => {
          const action = handleClose

          if (changed) {
            setAction(action)
            setReason('You have unsaved changes')
            setV2(true)
          } else {
            action()
          }
        }}
        PaperProps={{ style: { top: '53%', width: '66%' } }}
        title='New Task Category'
      >
        <div className={classes.root}>
          <ModalTextField title='Name' className={classes.input}>
            <input value={name} onChange={e => setName(e.target.value)} />
          </ModalTextField>

          <div className={classes.row}>
            <div className='input'>
              <div className='adorn'>Private Category</div>

              <Checkbox
                checked={isPrivate}
                onChange={e => setIsPrivate(e.target.checked)}
                size='small'
                color='primary'
                disabled={!canMakePrivate}
                className={classes.checkbox}
              />
            </div>
          </div>

          <div className={classes.note}>
            {isPrivate
              ? 'This will be a private category, only you will be able to see it.'
              : 'This will be a public category. Anyone will be able to see it and add tasks to it.'}
          </div>
        </div>
        {/* @ts-ignore */}
        <EndControls
          onSubmit={handleCreate}
          submitText='CREATE CATEGORY'
          SubmitIcon={() => null}
          hideDelete
        />
      </BaseModal>
    </>
  )
}

export default CategoryModal
